"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PackageSearch, Route, CircleCheck } from "lucide-react";
export default function HowItWorks() {
  const {
    ref: containerRef,
    isVisible: containerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const steps = [{
    number: 1,
    icon: PackageSearch,
    title: "Request Your Shipment",
    description: "Provide shipment details including origin, destination, cargo type, and timeline. We analyze your requirements to create an optimal logistics plan."
  }, {
    number: 2,
    icon: Route,
    title: "We Coordinate & Arrange",
    description: "Our team selects qualified carriers, plans efficient routes, and handles all documentation. Real-time tracking keeps you informed at every stage."
  }, {
    number: 3,
    icon: CircleCheck,
    title: "Delivery Completed",
    description: "Your cargo arrives safely and on schedule. We provide delivery confirmation and are available for any follow‑up support you need."
  }];
  return <section id="how-it-works" className="bg-background py-16 sm:py-20 lg:py-24" ref={containerRef}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`mb-12 text-center transition-all duration-[600ms] ease-out ${containerVisible ? "translate-y-0 opacity-100" : "translate-y-[10px] opacity-0"}`}>
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            How It Works
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white sm:text-lg">
            Three simple steps to reliable freight transportation coordination
          </p>
        </div>

        <div className="relative mx-auto max-w-3xl">
          <div className="absolute left-8 top-12 hidden h-[calc(100%-6rem)] w-0.5 bg-border md:block" style={{
          background: containerVisible ? "linear-gradient(to bottom, hsl(220, 80%, 50%) 0%, hsl(220, 80%, 50%) 100%)" : "linear-gradient(to bottom, hsl(0, 0%, 80%) 0%, hsl(0, 0%, 80%) 0%)",
          transition: "background 1200ms ease-out 200ms"
        }} />

          <div className="space-y-8">
            {steps.map((step, i) => {
            const {
              ref: stepRef,
              isVisible: stepVisible
            } = useRevealOnScroll({
              threshold: 0.3,
              once: true
            });
            const Icon = step.icon;
            return <div key={i} data-index={i} ref={stepRef} className={`transition-all duration-[600ms] ease-out ${stepVisible ? "translate-y-0 opacity-100" : "translate-y-[10px] opacity-0"}`} style={{
              transitionDelay: `${i * 100}ms`
            }}>
                  <Card className="relative overflow-hidden border border-border bg-card shadow-soft hover:-translate-y-1 hover:shadow-medium">
                    <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-start md:gap-6">
                      <div className="relative z-10 flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-primary text-foreground-primary shadow-medium transition-transform duration-[250ms] ease-[var(--ease-spring)] hover:scale-105">
                        <Icon className="h-8 w-8" strokeWidth={2} />
                      </div>

                      <div className="flex-1">
                        <CardHeader className="p-0">
                          <div className="mb-2 flex items-center gap-3">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-foreground-primary">
                              {step.number}
                            </span>
                            <CardTitle className="text-xl font-semibold text-white sm:text-2xl">
                              {step.title}
                            </CardTitle>
                          </div>
                          <CardDescription className="text-base text-white">
                            {step.description}
                          </CardDescription>
                        </CardHeader>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}